// frontend/src/features/assistant/assistantThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";

/**
 * askAssistant
 * - Envía: message + history (desde Redux) al backend
 * - Lee la respuesta en streaming (ReadableStream)
 * - Trocea los chunks grandes en trocitos para simular efecto "typing"
 * - Devuelve el texto completo al finalizar
 *
 * onChunk: callback opcional que recibe cada trozo parcial (lo usas en Assistant.jsx)
 */
export const askAssistant = createAsyncThunk(
  "assistant/askAssistant",
  async ({ message, onChunk }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authState = state.auth || {};
      const assistantState = state.assistant || {};

      const token = authState.token;
      const messages = assistantState.messages || [];

      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // Historial para el backend (evitamos duplicar el último mensaje del usuario)
      const historyForBackend = Array.isArray(messages) ? [...messages] : [];
      const lastMessage = historyForBackend[historyForBackend.length - 1];

      if (
        lastMessage &&
        lastMessage.role === "user" &&
        typeof lastMessage.content === "string" &&
        lastMessage.content === message
      ) {
        // Ya está en el historial, lo quitamos porque el backend añadirá message al final
        historyForBackend.pop();
      }

      const response = await fetch(`${backendUrl}/assistant/ask/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message,
          history: historyForBackend,
        }),
      });

      if (!response.ok) {
        return rejectWithValue(`Error HTTP ${response.status}`);
      }

      // Fallback: si por lo que sea no hay body en streaming
      if (!response.body) {
        const text = await response.text();
        if (onChunk && text) {
          onChunk(text);
        }
        return text;
      }

      // Lectura del stream oficial
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finalText = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const decodedChunk = decoder.decode(value, { stream: true });
        if (!decodedChunk) continue;

        // 🪄 Truco: troceamos el chunk para simular "escribiendo"
        const stepSize = 25; // nº de caracteres por trozo (ajusta a tu gusto)
        let offset = 0;

        while (offset < decodedChunk.length) {
          const piece = decodedChunk.slice(offset, offset + stepSize);
          finalText += piece;

          if (onChunk && piece) {
            onChunk(piece);
          }

          offset += stepSize;

          // Cedemos un pelín de tiempo al navegador para repintar
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      // Flush final del decoder por si queda algo pendiente
      const rest = decoder.decode();
      if (rest) {
        finalText += rest;
        if (onChunk) {
          onChunk(rest);
        }
      }

      return finalText;
    } catch (error) {
      console.error("Error en askAssistant:", error);
      return rejectWithValue(error.toString());
    }
  }
);
